/**
 * The one collaboration-config editor surface, in three presentations.
 *
 * `layout` is presentation ONLY — same form state, same validation, same single
 * submit. The default is what the Advanced page (`ConfigDetailPage`'s
 * Definition tab) has always rendered: name/description beside the schema
 * builders, definition editor below. `stacked-sections` (task 802) splits the
 * same content into two titled sections. `sub-navigated` (task 803, what the AI
 * Service Config tab uses) shows ONE of those sections at a time, chosen by the
 * caller's own segmented row — stacking still made a User scroll past a whole
 * canvas to reach the schema builders.
 *
 * **The hidden section stays mounted.** It is hidden with the `hidden`
 * attribute, never unmounted, because both sections belong to one submission: a
 * User who edits the input schema, switches to the graph view and saves must
 * find that schema edit in the update call, and unmounting would silently
 * discard it. `activeSection="none"` hides the whole form the same way, for a
 * caller showing a view of its own (the Config tab's Agents segment) without
 * destroying the un-saved edits behind it.
 *
 * **VIEW MODE (task 806).** With `viewModeEnabled`, an edit surface stops being
 * a permanently-armed form: every region renders as a READ-ONLY presentation
 * until the User asks to edit. The mode, the dirty signal and the three actions
 * that move between them are handed to the caller through `onViewStateChange`,
 * because the Edit / Save / Cancel controls belong in the caller's own panel
 * header — top-right of the Config tab, top-right of the Advanced Definition
 * tab — not at the bottom of the form. Create mode is unaffected: a form for a
 * record that does not exist yet has nothing to view.
 */
import { useNavigate } from 'react-router-dom';
import { useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useForm } from '../../hooks/useForm';
import { FormField, FormTextArea, SmartFormActions } from '../shared/form';
import { DefinitionCanvas } from '../definition-builder';
import { InputMappingEditor } from '../definition-builder/properties';
import { SchemaBuilder } from '../schema-builder';
import { SectionRail, type SectionRailEntry } from '../services/detail/SectionRail';
import { jsonSchemaToFields } from '../../utils/schemaBuilder';
import {
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import { CollaborationConfig } from '../../types';
import { CollaborationDefinition } from '../../types/definition';
import type { ValidationErrorDetail } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useFlag } from '../../hooks/useFeatureFlags';
import {
  isGraphDefinition,
  createEmptyDefinition,
  collaborationToReactFlow,
  reactFlowToCollaboration,
} from '../../utils/definitionTransform';

type EditorMode = 'visual' | 'json';

export interface ConfigFormData {
  name: string;
  description: string;
  definition: CollaborationDefinition;
}

interface ConfigFormProps {
  initialData?: Partial<CollaborationConfig>;
  onSubmit: (data: {
    name: string;
    description?: string;
    definition: Record<string, unknown>;
    input?: Record<string, unknown>;
    output?: Record<string, unknown>;
    outputMapping?: Record<string, string>;
  }) => Promise<void>;
  onCancel?: () => void;
  mode?: 'create' | 'edit';
  /** In edit mode, indicates if the name was changed in the header (for dirty tracking) */
  nameChanged?: boolean;
  /**
   * How the same fields are arranged. `default` is the Advanced page's
   * long-standing two-column layout; `stacked-sections` is two titled sections
   * (task 802); `sub-navigated` shows one section at a time (task 803).
   */
  layout?: ConfigFormLayout;
  /**
   * Which section shows, in the `sub-navigated` layout. `none` hides the whole
   * form — the caller is showing a view of its own — WITHOUT unmounting it, so
   * un-saved edits survive the detour.
   */
  activeSection?: ConfigFormSection;
  /**
   * Rendered on the right of the **Graph Config** header row
   * (`stacked-sections` only) — the caller's own chips and actions, so the
   * summary/Validate/Open-in-Advanced toolbar reads as part of the section
   * rather than a card floating above the editor.
   */
  graphSectionToolbar?: ReactNode;
  /**
   * Rendered directly beneath that header row (`stacked-sections` only) — the
   * caller's validation error list, which belongs to the definition and so to
   * this section.
   */
  graphSectionNotice?: ReactNode;
  /**
   * Present the config as a READ-ONLY VIEW first, editable only on request
   * (task 806). The bottom action row disappears with it: the caller places
   * Edit / Save / Cancel in its own panel header, driven by the state below.
   * Ignored in create mode.
   */
  viewModeEnabled?: boolean;
  /**
   * The mode, the dirty signal and the three actions, whenever any of them
   * changes. The handler identities are stable, so this fires only on a real
   * state change.
   */
  onViewStateChange?: (state: ConfigFormViewState) => void;
}

/**
 * What a `viewModeEnabled` form hands its caller so the caller can render the
 * panel-header controls (task 806).
 *
 * Deliberately state PLUS actions rather than a rendered control row: the two
 * surfaces that use this put the controls in different chrome, and neither of
 * them is inside this form's DOM.
 */
export interface ConfigFormViewState {
  /** True while the config is presented as a read-only view. */
  viewMode: boolean;
  /** Some region differs from the loaded config — deep-compared, not per-field. */
  isDirty: boolean;
  /** A save is in flight; both actions stay disabled until it settles. */
  isSubmitting: boolean;
  /**
   * The form would be accepted — required fields present, at least one node.
   * Save is SHOWN by `isDirty` and DISABLED by this, so a User who has changed
   * something into an unsaveable state still sees why nothing happens.
   */
  canSave: boolean;
  /** Turn the view back into the editors. */
  edit: () => void;
  /** Persist through the single existing update call. */
  save: () => void;
  /** Discard un-saved edits and return to the view. */
  cancel: () => void;
}

/** Every value one save writes — the unit the dirty check compares. */
interface ConfigBaseline {
  name: string;
  description: string;
  definition: CollaborationDefinition;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  outputMapping: Record<string, string> | null;
}

/**
 * The definition as a SAVE would write it (task 807).
 *
 * THE DEFECT THIS FIXES, found in the browser check and missed by every jsdom
 * test before it: opening Edit showed Save immediately, with nothing changed.
 *
 * The canvas round-trips a definition through its own representation, and that
 * round-trip is not key-identical to what was stored — `reactFlowToCollaboration`
 * gives every edge an `id` (generating one for stored edges that have none) and
 * writes `condition`, `label`, `settings`, `context` and `errorHandling` as
 * explicit `undefined`. So the moment the editable canvas mounted, it reported
 * a "change" that was really the same graph in canonical form, and the form
 * went dirty for having been opened.
 *
 * Comparing both sides in that canonical form is the fix: a re-serialization is
 * not an edit, and this is the same normalization the save itself performs.
 */
const canonicalDefinition = (definition: CollaborationDefinition): CollaborationDefinition => {
  const { nodes, edges } = collaborationToReactFlow(definition);
  return reactFlowToCollaboration(nodes, edges, definition);
};

/**
 * Deep value equality for the dirty check.
 *
 * Written out rather than compared as JSON text because key ORDER is not a
 * change: the canvas rebuilds the definition object on every edit, and a
 * stringified comparison would call a config dirty for having re-serialized the
 * same graph — putting a Save button on screen with nothing to save.
 */
const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }

  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every(
    (key) => Object.prototype.hasOwnProperty.call(right, key) && deepEqual(left[key], right[key])
  );
};

export type ConfigFormLayout = 'default' | 'stacked-sections' | 'sub-navigated';

/** The `sub-navigated` layout's views; `none` = the caller is showing its own. */
export type ConfigFormSection = 'graph' | 'schemas' | 'none';

/** The three sections the schemas view's left rail picks between (task 810). */
type SchemaSection = 'input' | 'output' | 'mapping';

/** "3 fields · 1 required" — the rail card's one line under a schema's name. */
const fieldSummary = (schema: Record<string, unknown> | null | undefined): string => {
  // The same converter the schema builder itself uses, so the count on the rail
  // and the field cards in the pane beside it can never disagree.
  const fields = jsonSchemaToFields(schema ?? null);
  if (fields.length === 0) return 'No fields defined';
  const required = fields.filter((field) => field.required).length;
  return `${fields.length} field${fields.length === 1 ? '' : 's'} · ${required} required`;
};

/** The same line for the mapping, counted in the unit it has: mapped keys. */
const mappingSummary = (mapping: Record<string, string> | null | undefined): string => {
  const count = mapping ? Object.keys(mapping).length : 0;
  if (count === 0) return 'No mapping defined';
  return `${count} field${count === 1 ? '' : 's'} mapped`;
};

// Helper to parse initial definition from API data
const parseInitialDefinition = (data?: Record<string, unknown>): CollaborationDefinition => {
  if (!data) {
    return createEmptyDefinition();
  }
  if (isGraphDefinition(data)) {
    return data;
  }
  // Legacy format - return empty definition (user can rebuild visually)
  return createEmptyDefinition();
};

export function ConfigForm({
  initialData,
  onSubmit,
  onCancel,
  mode = 'create',
  nameChanged = false,
  layout = 'default',
  activeSection = 'graph',
  graphSectionToolbar,
  graphSectionNotice,
  viewModeEnabled = false,
  onViewStateChange,
}: ConfigFormProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const [editorMode, setEditorMode] = useState<EditorMode>('visual');
  /**
   * THE DEFINITION BUILDER's lever (task 1217): the canvas, its properties
   * panel, node cards, validation overlay and transforms. Off, the definition
   * is edited as JSON (the form's existing JSON mode, with no way back to
   * Visual) and read as JSON; the rest of the form is unchanged.
   */
  const builderEnabled = useFlag('innovate.fe.configs.builder');
  const shownEditorMode: EditorMode = builderEnabled ? editorMode : 'json';

  /**
   * Which contract section the schemas view is showing (task 810). Local state
   * with a sensible default: the subtab itself is already addressable, and a
   * second addressing scheme underneath it would be one more thing to keep
   * honest for no one's benefit.
   */
  const [schemaSection, setSchemaSection] = useState<SchemaSection>('input');

  /** A create form has nothing to view yet, so the mode is edit-only there. */
  const supportsViewMode = viewModeEnabled && mode === 'edit';
  const [viewMode, setViewMode] = useState(supportsViewMode);

  // Track definition separately for the visual editor
  const [definition, setDefinition] = useState<CollaborationDefinition>(() =>
    parseInitialDefinition(initialData?.definition as Record<string, unknown> | undefined)
  );

  // JSON string representation for JSON editing mode
  const [definitionJson, setDefinitionJson] = useState<string>(() =>
    JSON.stringify(definition, null, 2)
  );
  const [definitionJsonError, setDefinitionJsonError] = useState<string | null>(null);

  /**
   * What the SERVER refused on the last save (task 774).
   *
   * The service rejects a bad definition with a precise per-field list — which
   * node, which mapping, and why. Before this it reached a console line and
   * nowhere else, so a User saw a dead Save button and no explanation. These
   * rows travel to the canvas, which renders them beside the client-side ones
   * and navigates to the node each names.
   */
  const [serverErrors, setServerErrors] = useState<ValidationErrorDetail[]>([]);

  // Schema states for input/output (managed by SchemaBuilder)
  const [inputSchema, setInputSchema] = useState<Record<string, unknown> | null>(
    (initialData?.input as Record<string, unknown>) || null
  );
  const [outputSchema, setOutputSchema] = useState<Record<string, unknown> | null>(
    (initialData?.output as Record<string, unknown>) || null
  );
  const [outputMapping, setOutputMapping] = useState<Record<string, string> | null>(
    (initialData?.outputMapping as Record<string, string>) || null
  );

  /**
   * THE SAVED STATE (task 806) — what the config is on the server, as far as
   * this form knows.
   *
   * It is what the read-only view renders, what the dirty check compares
   * against, and what Cancel restores. A successful save re-baselines it to the
   * values it just wrote, so the view shows the saved config immediately rather
   * than waiting on the list query to refetch.
   */
  const [baseline, setBaseline] = useState<ConfigBaseline>(() => ({
    name: initialData?.name || '',
    description: initialData?.description || '',
    definition: parseInitialDefinition(initialData?.definition as Record<string, unknown> | undefined),
    input: (initialData?.input as Record<string, unknown>) || null,
    output: (initialData?.output as Record<string, unknown>) || null,
    outputMapping: (initialData?.outputMapping as Record<string, string>) || null,
  }));
  // Read by the mode actions, which must see the CURRENT baseline without
  // being re-created (and re-notifying the caller) every time it changes.
  const baselineRef = useRef(baseline);
  baselineRef.current = baseline;

  const {
    values,
    errors,
    isSubmitting,
    isDirty,
    isValid,
    handleChange,
    handleSubmit,
    setFieldValue,
    reset,
  } = useForm<ConfigFormData>({
    // Derived from the baseline rather than from `initialData` directly, so
    // `reset()` — what Cancel runs — returns to the LAST SAVED state rather
    // than to whatever the config was when this form first mounted.
    initialValues: {
      name: baseline.name,
      description: baseline.description,
      definition: baseline.definition,
    },
    onSubmit: async (formValues) => {
      // Validate we have at least one node
      if (!definition.nodes || definition.nodes.length === 0) {
        throw new Error('Definition must have at least one node');
      }

      try {
        await onSubmit({
          name: formValues.name,
          description: formValues.description || undefined,
          definition: definition as unknown as Record<string, unknown>,
          input: inputSchema || undefined,
          output: outputSchema || undefined,
          outputMapping: outputMapping || undefined,
        });
        // A save that lands clears whatever the previous one was refused for.
        setServerErrors([]);
        // RE-BASELINE (task 806): what was just written IS the saved config, so
        // the form is no longer dirty and the read-only view shows the new
        // values without waiting for the query to refetch.
        setBaseline({
          name: formValues.name,
          description: formValues.description,
          definition,
          input: inputSchema,
          output: outputSchema,
          outputMapping,
        });
        if (supportsViewMode) setViewMode(true);
      } catch (error) {
        const apiError = error as Error & { validationErrors?: ValidationErrorDetail[] };
        setServerErrors(apiError.validationErrors ?? []);
        toast.error(
          mode === 'create' ? 'Could not create config' : 'Could not save changes',
          apiError.message
        );
        // Rethrown so `useForm` treats this as a failed submit and does not
        // reset the form to its initial values on a save that did not happen.
        throw error;
      }
    },
    requiredFields: ['name'],
    validate: (formValues) => {
      const validationErrors: Partial<Record<keyof ConfigFormData, string>> = {};
      if (!formValues.name.trim()) {
        validationErrors.name = 'Config name is required';
      }
      return validationErrors;
    },
  });

  // Handle definition canvas changes
  const handleDefinitionChange = useCallback(
    (definition: CollaborationDefinition) => {
      setDefinition(definition);
      setFieldValue('definition', definition);
      // The rejected shape no longer exists once the graph is edited; keeping
      // the old rows would point at nodes the User has already changed.
      setServerErrors([]);
      // Keep JSON in sync when in visual mode
      setDefinitionJson(JSON.stringify(definition, null, 2));
      setDefinitionJsonError(null);
    },
    [setFieldValue]
  );

  // Handle JSON textarea changes
  const handleJsonChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setDefinitionJson(value);

      // Try to parse and validate JSON
      try {
        const parsed = JSON.parse(value);
        if (isGraphDefinition(parsed)) {
          setDefinitionJsonError(null);
          setDefinition(parsed);
          setFieldValue('definition', parsed);
        } else {
          setDefinitionJsonError('Invalid definition format. Must have version, nodes, and edges.');
        }
      } catch {
        setDefinitionJsonError('Invalid JSON format');
      }
    },
    [setFieldValue]
  );

  // Handle editor mode toggle
  const handleModeToggle = useCallback(
    (newMode: EditorMode) => {
      if (newMode === editorMode) return;

      if (newMode === 'visual' && definitionJsonError) {
        // Don't switch to visual mode if JSON is invalid
        return;
      }

      if (newMode === 'json') {
        // Sync JSON from current visual state
        setDefinitionJson(JSON.stringify(definition, null, 2));
        setDefinitionJsonError(null);
      }

      setEditorMode(newMode);
    },
    [editorMode, definitionJsonError, definition]
  );

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/configs');
    }
  };

  // Check if the definition has any nodes
  const hasNodes = definition.nodes && definition.nodes.length > 0;

  /**
   * Dirty by VALUE, against the saved config (task 806).
   *
   * `useForm`'s own `isDirty` is a flag set by any keystroke and covers only the
   * fields it holds — it says nothing about the schemas, the mapping or the
   * graph, and it stays true after a manual revert. A deep compare against the
   * baseline answers the question the Save button actually asks: is there
   * anything to write?
   */
  const isValueDirty = useMemo(
    () =>
      !deepEqual(
        {
          name: values.name,
          description: values.description,
          definition: canonicalDefinition(definition),
          input: inputSchema,
          output: outputSchema,
          outputMapping,
        },
        {
          ...baseline,
          definition: canonicalDefinition(baseline.definition),
        }
      ),
    [values.name, values.description, definition, inputSchema, outputSchema, outputMapping, baseline]
  );

  // Track if form is dirty including name changes from header. The Advanced
  // page edits the name in its own title control, so that change reaches the
  // dirty signal as a flag rather than through the compare above.
  const isFormDirty = (supportsViewMode ? isValueDirty : isDirty) || nameChanged;

  /**
   * Put every editable value back to the saved config.
   *
   * Run on BOTH transitions, which is what keeps them symmetric: Cancel
   * discards, and Edit re-syncs before opening — so a form entered a second
   * time after a save never shows what `useForm` reset itself to on the way out.
   */
  // Same reason as `handleSubmitRef` below: `useForm` rebuilds these on every
  // render, and this callback must stay stable to keep the caller's state
  // notification from firing on every render.
  const formApiRef = useRef({ reset, setFieldValue });
  formApiRef.current = { reset, setFieldValue };

  const restoreBaseline = useCallback(() => {
    const saved = baselineRef.current;
    const { reset: resetForm, setFieldValue: setValue } = formApiRef.current;
    resetForm();
    setValue('name', saved.name);
    setValue('description', saved.description);
    setValue('definition', saved.definition);
    setDefinition(saved.definition);
    setDefinitionJson(JSON.stringify(saved.definition, null, 2));
    setDefinitionJsonError(null);
    setInputSchema(saved.input);
    setOutputSchema(saved.output);
    setOutputMapping(saved.outputMapping);
    setServerErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enterEditMode = useCallback(() => {
    restoreBaseline();
    setViewMode(false);
  }, [restoreBaseline]);

  const cancelEdit = useCallback(() => {
    restoreBaseline();
    setViewMode(true);
  }, [restoreBaseline]);

  /**
   * Submit from OUTSIDE the form element (task 806): the Save control lives in
   * the caller's panel header, so there is no submit event to forward and the
   * shared `useForm` handler is given the one thing it uses from one.
   */
  // `useForm` builds a fresh `handleSubmit` on every render, so it is read from
  // a ref: a submit action whose identity changed each render would re-notify
  // the caller each render, and a caller that stores what it is handed would
  // then re-render forever.
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  const submitFromChrome = useCallback(() => {
    void handleSubmitRef.current({
      preventDefault: () => undefined,
    } as unknown as React.FormEvent);
  }, []);

  /**
   * Hand the caller the state its header controls render from. The three
   * actions are stable, so this fires only when the mode, the dirty signal or
   * the in-flight save actually changes.
   */
  // Held in a ref so a caller that passes an inline arrow cannot turn this into
  // a render loop: the effect below depends on the STATE, never on the handler.
  const onViewStateChangeRef = useRef(onViewStateChange);
  onViewStateChangeRef.current = onViewStateChange;

  useEffect(() => {
    if (!supportsViewMode) return;
    onViewStateChangeRef.current?.({
      viewMode,
      isDirty: isFormDirty,
      isSubmitting,
      canSave: isValid && Boolean(hasNodes),
      edit: enterEditMode,
      save: submitFromChrome,
      cancel: cancelEdit,
    });
  }, [
    supportsViewMode,
    viewMode,
    isFormDirty,
    isSubmitting,
    isValid,
    hasNodes,
    enterEditMode,
    submitFromChrome,
    cancelEdit,
  ]);

  // ---------------------------------------------------------------------------
  // Read-only presentations (task 806)
  //
  // Not the editors with their inputs disabled — a real view. A disabled input
  // still reads as a field someone forgot to enable, and it still puts a text
  // box on screen where the description is only being read.
  // ---------------------------------------------------------------------------

  /**
   * The identity block as plain text.
   *
   * The name appears here only in create mode, mirroring the editor exactly:
   * an existing config's name is owned by the caller's own chrome — the
   * Advanced page's editable title, the Config tab's summary chip — and
   * repeating it inside the form would put the same string on screen twice.
   */
  const identityViewRegion = (
    <div className="space-y-4" data-testid="config-view-identity">
      {mode === 'create' && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Name
          </p>
          <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
            {baseline.name || (
              <span className="italic text-gray-500 dark:text-gray-400">Unnamed config</span>
            )}
          </p>
        </div>
      )}
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Description
        </p>
        <p
          data-testid="config-view-description"
          className="mt-1 whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100"
        >
          {baseline.description || (
            <span className="italic text-gray-500 dark:text-gray-400">No description</span>
          )}
        </p>
      </div>
    </div>
  );

  /**
   * The schemas through `SchemaBuilder`'s own `readOnly` presentation — the
   * existing affordance, so the read view and the editor can never disagree
   * about what a schema contains. The output mapping has no such affordance, so
   * it gets a compact field-list read view in the style of the agents dossier's
   * I/O contract panel.
   */
  // Named separately from the block below because the railed schemas view
  // (task 810) shows this same read view on its own, as one pane of three.
  const outputMappingViewRegion = (
    <div>
      <p className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
        Output Mapping
      </p>
      {baseline.outputMapping && Object.keys(baseline.outputMapping).length > 0 ? (
        <ul className="flex flex-col gap-1.5" data-testid="config-view-output-mapping">
          {Object.entries(baseline.outputMapping).map(([field, source]) => (
            <li key={field} className="flex flex-wrap items-baseline gap-x-2 text-xs">
              <span className="font-mono text-indigo-700 dark:text-indigo-300">{field}</span>
              <span className="text-gray-400 dark:text-gray-500">←</span>
              <span className="font-mono text-gray-600 dark:text-gray-300">{source}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-500 dark:text-gray-400">No output mapping defined.</p>
      )}
    </div>
  );

  const schemaViewRegion = (
    <div className="space-y-6" data-testid="config-view-schemas">
      <SchemaBuilder
        label="Input Schema"
        value={baseline.input}
        onChange={() => undefined}
        readOnly
      />

      <SchemaBuilder
        label="Output Schema"
        value={baseline.output}
        onChange={() => undefined}
        readOnly
      />

      {outputMappingViewRegion}
    </div>
  );

  /**
   * THE DEFINITION, READ-ONLY (task 807).
   *
   * The graph, not JSON. A collaboration definition is read by looking at its
   * nodes and the edges between them; the JSON is the serialization, and the
   * task numbered 806 shipped it here only because the canvas had no read-only
   * mode. It has one now, so the view shows the same canvas the editor does —
   * at the layout the definition stores — with every mutation affordance gone.
   *
   * `onChange` is deliberately the REAL handler rather than a no-op: if the
   * read-only canvas ever emitted a change, the form would go dirty and the
   * Save button would appear, which is exactly the failure a silent no-op would
   * hide from its own tests.
   */
  const definitionViewRegion = (
    <div>
      <p className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Definition</p>
      <div className="overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600">
        <DefinitionCanvas
          readOnly
          value={baseline.definition}
          onChange={handleDefinitionChange}
        />
      </div>
      {/* No node/edge counts here: the callers that have a summary toolbar
          already show them, and repeating a count is how the same number ends
          up on screen twice saying slightly different things. */}
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Read-only. Choose Edit to change the graph.
      </p>
    </div>
  );

  /**
   * True when the stored definition is present but not a graph the canvas can
   * draw. `parseInitialDefinition` turns that case into an EMPTY definition, so
   * the check has to read the raw stored value rather than the baseline.
   */
  const definitionIsUnparseable =
    Boolean(initialData?.definition) &&
    !isGraphDefinition(initialData?.definition as Record<string, unknown>);

  /**
   * The fallback, and ONLY the fallback (task 807).
   *
   * A stored definition that is not a graph — a legacy shape the canvas cannot
   * render — would otherwise show as an empty canvas, which reads as "this
   * collaboration has no nodes" when the truth is "this cannot be drawn". The
   * raw JSON is shown instead, so nothing is hidden. It is also the read view
   * with the Definition Builder flag off (task 1217), without the warning.
   */
  const definitionFallbackRegion = (
    <div>
      <p className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Definition</p>
      {definitionIsUnparseable && (
        <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
          This definition is not in the graph format the canvas draws, so it is shown as stored.
        </p>
      )}
      <pre
        data-testid="config-view-definition-json"
        className="max-h-[calc(100vh-320px)] min-h-[200px] overflow-auto rounded-lg border border-gray-300 bg-gray-50 p-4 font-mono text-xs text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
      >
        {JSON.stringify(
          (definitionIsUnparseable ? initialData?.definition : baseline.definition) ?? {},
          null,
          2
        )}
      </pre>
    </div>
  );

  /** Name (create mode only) and description — they identify the record. */
  const identityRegion = (
    <div className="space-y-6">
      {mode === 'create' && (
        <FormField
          label="Config Name"
          name="name"
          type="text"
          value={values.name}
          onChange={handleChange}
          error={errors.name}
          required
          placeholder="Enter config name"
        />
      )}

      <FormTextArea
        label="Description"
        name="description"
        value={values.description}
        onChange={handleChange}
        error={errors.description}
        placeholder="Enter config description"
        rows={mode === 'create' ? 3 : 6}
      />
    </div>
  );

  /** What the service accepts, returns, and how node output maps onto it. */
  const schemaRegion = (
    <div className="space-y-6">
      <SchemaBuilder
        label="Input Schema (optional)"
        value={inputSchema}
        onChange={setInputSchema}
      />

      <SchemaBuilder
        label="Output Schema (optional)"
        value={outputSchema}
        onChange={setOutputSchema}
      />

      <InputMappingEditor
        label="Output Mapping"
        helpText="Map where each output field's value comes from after all nodes complete"
        value={outputMapping || undefined}
        onChange={(mapping) => setOutputMapping(mapping || null)}
        schema={outputSchema}
      />
    </div>
  );

  /**
   * THE SCHEMAS VIEW AS MASTER–DETAIL (task 810).
   *
   * Three sections that are each long enough to bury the ones beneath them: a
   * real service's input schema is eight field cards before the output schema
   * is even on screen. The Agents subtab beside this one already answers that
   * shape with a rail of cards and one section's content at a time, so this
   * wears the same clothes rather than inventing a second list style.
   *
   * Each pane holds the section's EXISTING content, untouched — the field
   * cards, the View/Edit-as-JSON toggle, the mapping editor. Nothing about how
   * a schema is edited or saved changes here; only how many of them are on
   * screen at once.
   */
  const schemaSectionPanes: Record<SchemaSection, { view: ReactNode; edit: ReactNode }> = {
    input: {
      view: (
        <SchemaBuilder
          label="Input Schema"
          value={baseline.input}
          onChange={() => undefined}
          readOnly
        />
      ),
      edit: (
        <SchemaBuilder
          label="Input Schema (optional)"
          value={inputSchema}
          onChange={setInputSchema}
        />
      ),
    },
    output: {
      view: (
        <SchemaBuilder
          label="Output Schema"
          value={baseline.output}
          onChange={() => undefined}
          readOnly
        />
      ),
      edit: (
        <SchemaBuilder
          label="Output Schema (optional)"
          value={outputSchema}
          onChange={setOutputSchema}
        />
      ),
    },
    mapping: {
      view: outputMappingViewRegion,
      edit: (
        <InputMappingEditor
          label="Output Mapping"
          helpText="Map where each output field's value comes from after all nodes complete"
          value={outputMapping || undefined}
          onChange={(mapping) => setOutputMapping(mapping || null)}
          schema={outputSchema}
        />
      ),
    },
  };

  /**
   * The rail's summary lines, counted off whatever the pane beside them is
   * showing — the baseline in view mode, the live values while editing — so the
   * count and the content can never disagree.
   */
  const schemaSectionEntries: SectionRailEntry[] = [
    {
      id: 'input',
      label: 'Input Schema',
      summary: fieldSummary(viewMode ? baseline.input : inputSchema),
      icon: ArrowRightOnRectangleIcon,
    },
    {
      id: 'output',
      label: 'Output Schema',
      summary: fieldSummary(viewMode ? baseline.output : outputSchema),
      icon: ArrowLeftOnRectangleIcon,
    },
    {
      id: 'mapping',
      label: 'Output Mapping',
      summary: mappingSummary(viewMode ? baseline.outputMapping : outputMapping),
      icon: ArrowsRightLeftIcon,
    },
  ];

  const schemaRailBlock = (
    <div className="flex flex-col gap-5 md:flex-row md:items-start">
      {/* Below md the rail stacks above the pane; at md and up it holds its
          width and sticks, exactly as the Agents rail does — the two subtabs
          have to read as one design. */}
      <div className="md:w-[260px] md:shrink-0 md:sticky md:top-4">
        <SectionRail
          ariaLabel="Input, output and mapping sections"
          testId="config-schema-rail"
          heading="Contract"
          entries={schemaSectionEntries}
          selectedId={schemaSection}
          onSelect={(id) => setSchemaSection(id as SchemaSection)}
        />
      </div>

      {/* All three panes stay MOUNTED and are hidden with the `hidden`
          attribute — the same rule the task numbered 803 set for the
          sub-navigation above them, and for the same reason: this is one form
          with one save, so switching sections must not be a way to lose an
          edit. Unmounting the pane a User just typed in would silently drop it
          from the update call. */}
      <div className="min-w-0 flex-1">
        {(Object.keys(schemaSectionPanes) as SchemaSection[]).map((section) => (
          <div
            key={section}
            data-testid={`config-schema-pane-${section}`}
            hidden={schemaSection !== section}
          >
            {viewMode ? schemaSectionPanes[section].view : schemaSectionPanes[section].edit}
          </div>
        ))}
      </div>
    </div>
  );

  /** The graph itself: the Visual/JSON toggle and the editor it chooses. */
  const definitionRegion = (
    <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Definition <span className="text-red-500">*</span>
          </label>
          {builderEnabled && (
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                type="button"
                onClick={() => handleModeToggle('visual')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  editorMode === 'visual'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                Visual
              </button>
              <button
                type="button"
                onClick={() => handleModeToggle('json')}
                disabled={editorMode === 'visual' && false}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  editorMode === 'json'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                JSON
              </button>
            </div>
          )}
        </div>

        {shownEditorMode === 'visual' ? (
          <>
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              <DefinitionCanvas
                value={definition}
                onChange={handleDefinitionChange}
                serverErrors={serverErrors}
              />
            </div>
            {!hasNodes && (
              <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                Drag nodes from the toolbar above to build your collaboration
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Visual editor. Drag nodes to add them, then connect them by dragging from one handle to another.
            </p>
          </>
        ) : (
          <>
            <textarea
              value={definitionJson}
              onChange={handleJsonChange}
              data-testid="config-definition-json"
              rows={20}
              className={`block w-full rounded-lg shadow-sm font-mono text-sm ${
                definitionJsonError
                  ? 'border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 dark:border-gray-600 focus:ring-indigo-500 focus:border-indigo-500'
              } dark:bg-gray-700 dark:text-white`}
              placeholder='{\n  "version": "1.0",\n  "nodes": [],\n  "edges": []\n}'
            />
            {definitionJsonError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{definitionJsonError}</p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Edit the definition directly as JSON.
              {builderEnabled && ' Switch to Visual mode to use the drag-and-drop editor.'}
            </p>
          </>
        )}
    </div>
  );

  /**
   * Which presentation each region gets. In view mode every one of them is the
   * read-only rendering; the editors are simply not on screen, so there is
   * nothing to type into (task 806).
   */
  const identityBlock = viewMode ? identityViewRegion : identityRegion;
  const schemaBlock = viewMode ? schemaViewRegion : schemaRegion;
  // With the builder off (task 1217) the read view is the JSON as well.
  const definitionBlock = viewMode
    ? definitionIsUnparseable || !builderEnabled
      ? definitionFallbackRegion
      : definitionViewRegion
    : definitionRegion;

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
      data-testid="config-form"
      data-mode={viewMode ? 'view' : 'edit'}
      // Hidden, never unmounted — see the module note: this is what preserves
      // un-saved edits while the caller shows a view of its own.
      hidden={layout === 'sub-navigated' && activeSection === 'none'}
    >
      {layout === 'sub-navigated' ? (
        <>
          {/* One view at a time. The caller's segmented row above names each
              view, so neither section repeats a heading here. */}
          <section
            data-testid="service-config-graph-section"
            hidden={activeSection !== 'graph'}
            className="space-y-4"
          >
            {(graphSectionToolbar || graphSectionNotice) && (
              <div>
                {graphSectionToolbar}
                {graphSectionNotice}
              </div>
            )}

            {identityBlock}
            {definitionBlock}
          </section>

          <section
            data-testid="service-config-schemas-section"
            hidden={activeSection !== 'schemas'}
            className="space-y-4"
          >
            <p className="text-sm text-gray-500 dark:text-gray-400">
              What the service accepts as turn input and returns as turn output, and how the
              nodes' results map onto that output.
            </p>

            {/* Master–detail here and ONLY here (task 810). The Advanced page's
                `stacked-sections` and `default` layouts keep the stack: they
                are laid out for reading the whole record at once. */}
            {schemaRailBlock}
          </section>
        </>
      ) : layout === 'stacked-sections' ? (
        <>
          {/* Section headers match the Agents fold on the same tab, so all
              three blocks read in one rhythm. */}
          <section data-testid="service-config-graph-section" className="space-y-4">
            <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                    Graph Config
                  </h2>
                  <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                    How this collaboration is wired — the nodes that run when the service is
                    called, and the edges between them.
                  </p>
                </div>
                {graphSectionToolbar}
              </div>
              {graphSectionNotice}
            </div>

            {identityBlock}
            {definitionBlock}
          </section>

          <section data-testid="service-config-schemas-section" className="space-y-4">
            <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                Input / Output / Mapping schemas
              </h2>
              <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                What the service accepts as turn input and returns as turn output, and how the
                nodes' results map onto that output.
              </p>
            </div>

            {schemaBlock}
          </section>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {identityBlock}
            {schemaBlock}
          </div>

          {definitionBlock}
        </>
      )}

      {/* One save for both sections — they are one record.
          A `viewModeEnabled` caller owns the actions instead, in its own panel
          header (task 806), so the bottom row is not rendered there at all. */}
      {!supportsViewMode && (
        <SmartFormActions
          mode={mode}
          isDirty={isFormDirty}
          isValid={isValid && hasNodes}
          isSubmitting={isSubmitting}
          onCancel={handleCancel}
          submitLabel={mode === 'create' ? 'Create Config' : 'Save Changes'}
        />
      )}
    </form>
  );
}
